import { useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";
import { buildStartViewingCommandPayload, getStartViewingCommandError } from "../lib/startViewingCommand";

/**
 * Step 1 of live playback — call executeSurveillanceCommand (startViewing) per camera.
 * Each invocation uses its own WS request id so multiple cells can run in parallel.
 */
export function useStartViewingCommand({
  standalone,
  entId,
  orgId,
  siteId,
  isAuthReady,
}) {
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const pendingRef = useRef({});

  useEffect(() => {
    Object.keys(pendingRef.current).forEach((requestUuid) => {
      if (!(requestUuid in dataMap)) return;

      const response = getData(requestUuid);
      const pending = pendingRef.current[requestUuid];
      delete pendingRef.current[requestUuid];

      if (!pending) return;

      const commandError = getStartViewingCommandError(response);
      if (commandError) {
        pending.reject(new Error(commandError));
        return;
      }

      pending.resolve(response);
    });
  }, [dataMap, getData]);

  const requestStartViewing = useCallback(
    ({ deviceId, streamId = 1 }) => {
      if (!isAuthReady || !orgId || !deviceId) {
        return Promise.reject(new Error("Missing scope or device for startViewing"));
      }

      const requestUuid = uuidv4();
      const hasSelectedSite = Boolean(siteId);
      const apiKey = hasSelectedSite
        ? standalone
          ? "/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update"
          : "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/executeSurveillanceCommand.update"
        : "/orgs/:orgId/devices/:deviceId/executeCommand.update";
      const restfulParams = hasSelectedSite
        ? standalone
          ? { orgId, siteId, deviceId }
          : { entId, orgId, siteId, deviceId }
        : { orgId, deviceId };

      return new Promise((resolve, reject) => {
        pendingRef.current[requestUuid] = { deviceId, resolve, reject };
        sendRequest(
          apiKey,
          requestUuid,
          restfulParams,
          buildStartViewingCommandPayload(streamId)
        );
      });
    },
    [standalone, entId, orgId, siteId, isAuthReady, sendRequest]
  );

  return requestStartViewing;
}
